import {PageHeader} from "../menu/PageHeader";
import {Badge, Button, Card, Col, Container, Row} from "react-bootstrap";
import React, {useContext, useEffect, useState} from "react";
import {Link, useNavigate, useSearchParams} from "react-router-dom";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {UsacmContext} from "../App";
import {getCourseCart, getCourseCatalogCourse} from "../api/CourseApi";
import {getErrorMessageForField} from "../utils/formUtils";
import {CONF_LABEL_KEY_COURSE, CONF_LABEL_KEY_COURSE_PAGE} from "../constants";
import {getConfLabel, makePlural, titleCase} from "../utils/usacmUtils";
import {CoursePriceList} from "./CoursePriceList";
import "./CourseList.scss";

/**
 * A course's own page - /course?id=N.  Opened from a catalog card, and the place to link to a course from outside
 * the site.  Shows the whole description and the list of pages, and the course can be bought or opened from here.
 */
export function CourseDetail() {
  const context = useContext(UsacmContext);
  const [conf,] = context.conference;
  const [navChanged,] = context.navigationChanged;
  const [cartCount, setCartCount] = context.courseCartCount;
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const courseId = searchParams.get('id');
  const courseLabel = titleCase(getConfLabel(conf, CONF_LABEL_KEY_COURSE));
  const pageLabel = titleCase(getConfLabel(conf, CONF_LABEL_KEY_COURSE_PAGE));
  const [course, setCourse] = useState(null);
  const [errors, setErrors] = useState([]);

  function loadCourse() {
    getCourseCatalogCourse(courseId, (status, data, newErrors) => {
      if (status === 200) {
        setCourse(data);
        setErrors([]);
      } else {
        setCourse(null);
        setErrors(newErrors);
      }
    });
    getCourseCart((status, data) => {
      if (status === 200) {
        setCartCount(data.items.length);
      }
    });
  }

  useEffect(() => {
    loadCourse();
  }, [navChanged, courseId]);

  function getPurchaseContent() {
    if (course.has_access) {
      return <div className="mb-3">You are enrolled in this {courseLabel.toLowerCase()}.</div>;
    }
    if (course.pending_review) {
      return <div className="mb-3">Your purchase is waiting to be approved.</div>;
    }
    if (course.prices.length === 0) {
      return <div className="mb-3">This {courseLabel.toLowerCase()} is not available right now.</div>;
    }
    return null;
  }

  return (
    <div>
      <PageHeader pageTitle={course ? course.title : courseLabel}/>
      <Container fluid className="usacm-container-wide">

        <div className="d-flex justify-content-between align-items-center mb-3">
          <Link to="/course-list">
            <FontAwesomeIcon icon="fa-arrow-left" className="me-2"/>All {makePlural(courseLabel)}
          </Link>
          <Button type="button" variant="outline-primary" onClick={() => navigate('/course-cart')}>
            <FontAwesomeIcon icon="fa-cart-shopping" className="me-2"/>Cart ({cartCount})
          </Button>
        </div>

        {getErrorMessageForField(errors, '') &&
          <Row>
            <Col className="text-center mb-3 usacm-error-message">
              {getErrorMessageForField(errors, '')}
            </Col>
          </Row>
        }

        {course &&
          <Row>
            <Col lg={8} className="mb-4">
              {course.image_url &&
                <img src={course.image_url} alt="" className="course-detail-image mb-3"/>
              }
              {course.subtitle && <h4 className="text-muted mb-3">{course.subtitle}</h4>}
              {course.description &&
                <div className="course-detail-description mb-4" dangerouslySetInnerHTML={{__html: course.description}}/>
              }

              {course.pages.length > 0 &&
                <>
                  <h5>{makePlural(pageLabel)}</h5>
                  <ol className="course-detail-outline">
                    {course.pages.map(page => <li key={page.id}>{page.title}</li>)}
                  </ol>
                </>
              }
            </Col>

            <Col lg={4}>
              <Card className="course-detail-purchase">
                <Card.Body>
                  <Card.Title>
                    {course.has_access && <Badge bg="success">Enrolled</Badge>}
                    {!course.has_access && course.pending_review &&
                      <Badge bg="warning" text="dark">Pending Review</Badge>
                    }
                    {!course.has_access && !course.pending_review && 'Enroll'}
                  </Card.Title>
                  {getPurchaseContent()}
                  {!course.has_access && !course.pending_review &&
                    <CoursePriceList course={course} onChanged={loadCourse} setErrors={setErrors}/>
                  }
                  {course.can_view_content &&
                    <div className="d-grid mt-3">
                      <Button type="button" onClick={() => navigate('/my-course-list?courseid=' + course.id)}>
                        View {courseLabel}
                      </Button>
                    </div>
                  }
                </Card.Body>
              </Card>
            </Col>
          </Row>
        }

      </Container>
    </div>
  );
}
